import { APIProvider, AdvancedMarker, Map, MapMouseEvent, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEffect, useRef } from 'react'
import MarkerIcon from '../assets/images/marker.svg'

const API_KEY = process.env.REACT_APP_GOOGLEMAPAPIKEY ?? ''
const MAP_ID = process.env.REACT_APP_GOOGLEMAPID ?? 'DEMO_MAP_ID'

const mapCenter = { lat: 45.8130174, lng: 15.9773833 }
const defaultMapHeight = 'calc(100vh - 320px)'

interface LatLng {
    lat: number
    lng: number
}

interface MapContainerProps {
    marker?: LatLng
    address?: string
    showRadius?: boolean
    height?: string
    handleAddressSearchDone?: (location: LatLng) => void
    handleMapMarkerOnMove?: (lat: number, lng: number) => void
}

interface MapChildProps extends Omit<MapContainerProps, 'height'> {
    onAddressFound: (coords: LatLng) => void
}

const MapChild = (props: MapChildProps) => {
    const { marker, address, showRadius, handleMapMarkerOnMove, onAddressFound } = props

    const geocodingLib = useMapsLibrary('geocoding')
    const map = useMap()

    // Effect to pan map when marker moves to a new position
    useEffect(() => {
        if (!marker || !map) return

        map.panTo(marker)
    }, [marker, map])

    // Effect to geocode address and pan map to found location
    useEffect(() => {
        if (!address || !geocodingLib) return

        const geocoder = new geocodingLib.Geocoder()

        geocoder.geocode({ address }, (results, status) => {
            if (status !== 'OK' || !results?.[0]) return

            const location = results[0].geometry.location
            const coords = { lat: location.lat(), lng: location.lng() }
            map?.panTo(coords)
            onAddressFound(coords)
        })
    }, [address, geocodingLib, onAddressFound, map])

    // Effect to draw a radius circle around the marker when approximate location is enabled
    useEffect(() => {
        if (!map || !marker || !showRadius) return

        const circle = new google.maps.Circle({
            map,
            center: marker,
            radius: 100,
            strokeColor: '#FF0000',
            strokeOpacity: 0.2,
            fillColor: '#FF0000',
            fillOpacity: 0.2,
        })

        return () => circle.setMap(null)
    }, [map, marker, showRadius])

    const handleDragEnd = (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || !handleMapMarkerOnMove) return

        const lat = e.latLng.lat()
        const lng = e.latLng.lng()

        handleMapMarkerOnMove(lat, lng)
    }

    if (!showRadius && marker) {
        return (
            <AdvancedMarker draggable position={marker} onDragEnd={handleDragEnd}>
                <img src={MarkerIcon} width={55} height={55} alt="marker" />
            </AdvancedMarker>
        )
    }

    return null
}

const MapContainer = (props: MapContainerProps) => {
    const { marker, address, showRadius = false, handleAddressSearchDone, handleMapMarkerOnMove, height } = props

    const handleClick = (e: MapMouseEvent) => {
        if (!e.detail.latLng || !handleMapMarkerOnMove) return

        handleMapMarkerOnMove(e.detail.latLng.lat, e.detail.latLng.lng)
    }

    const onAddressFound = (coords: LatLng) => {
        if (handleAddressSearchDone) handleAddressSearchDone(coords)
    }

    return (
        <div id="gmaps-markers" className="gmaps" style={{ position: 'relative', height: height ?? defaultMapHeight }}>
            <APIProvider apiKey={API_KEY}>
                <Map
                    style={{ width: '100%', height: '100%' }}
                    defaultZoom={17}
                    defaultCenter={marker ?? mapCenter}
                    mapId={MAP_ID}
                    onClick={handleClick}
                >
                    <MapChild
                        marker={marker}
                        address={address}
                        showRadius={showRadius}
                        handleAddressSearchDone={handleAddressSearchDone}
                        handleMapMarkerOnMove={handleMapMarkerOnMove}
                        onAddressFound={onAddressFound}
                    />
                </Map>
            </APIProvider>
        </div>
    )
}

export default MapContainer
